import { createContext, useContext, useEffect, useState } from "react";

interface BackendUrlContextType {
  isLoading: boolean;
  error?: Error;

  /** URL directly to the backend, skipping any CDN steps */
  backendUrl?: string;
}

const ctx = createContext<BackendUrlContextType | undefined>(undefined);

/**
 * Provides the URL directly to the backend, skipping the CDN that imposes a 30s time limit.
 *
 * @see useBackendUrl
 */
export function BackendUrlProvider({ children }: React.PropsWithChildren) {
  const [backendUrl, setBackendUrl] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  useEffect(() => {
    let cancelled = false;
    const loadBaseUrl = async () => {
      try {
        const resp = await fetch(`${import.meta.env.REACT_APP_BACKEND_URL}readyz`);
        if (!resp.ok) {
          throw new Error("backend is not ready 😬");
        }
        const url = resp.headers.get("x-cc-backend-base-url");
        if (!cancelled) {
          setBackendUrl(url || import.meta.env.REACT_APP_BACKEND_URL);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadBaseUrl();
    return () => {
      cancelled = true;
    };
  }, []);
  return <ctx.Provider value={{ backendUrl, error, isLoading }}>{children}</ctx.Provider>;
}

/**
 * Get a URL directly to the backend for things that don't work well with the
 * CDN layer.
 *
 * @see BackendUrlProvider
 */
export const useBackendUrl = (): BackendUrlContextType => {
  const context = useContext(ctx);
  if (!context) {
    throw new Error("useBaseUrl must be used within a BackendUrlProvider");
  }
  return context;
};
